import React, { useState } from 'react';
import { Stack, Switch, FormControlLabel, TableRow, TableCell, Typography } from '@mui/material';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import { useAdminTheme } from '../../admin-theme';
import { stagingApiBase } from './stagingOrg';

// Option toggles for the sellable and restricted rules. The options endpoint
// REPLACES the whole options JSON, so every save spreads the existing options.

interface OptionRule {
  fieldName: string;
  options?: Record<string, any> | null;
}

interface ToggleDef {
  key: string;
  label: string;
  message: (on: boolean) => string;
}

// Both default to on when the option is absent.
const TOGGLES: Record<string, ToggleDef> = {
  sellable: {
    key: 'offImmediate',
    label: 'Switching sell-in-app OFF applies immediately',
    message: (on) => `Switching sell-in-app OFF ${on ? 'applies immediately' : 'now needs approval'}`,
  },
  restricted: {
    key: 'defaultOn',
    label: 'New products are 18+ by default',
    message: (on) => `New products are ${on ? '18+' : 'not 18+'} by default`,
  },
};

export default function RuleOptionRows({ rule, onChanged }: { rule: OptionRule; onChanged?: () => void }) {
  const { colors } = useAdminTheme();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const def = TOGGLES[rule.fieldName];
  if (!def) return null;

  const checked = rule.options?.[def.key] ?? true;

  const toggle = async (value: boolean) => {
    setBusy(true);
    try {
      await authInstance.put(`${stagingApiBase()}/staging/rules/${rule.fieldName}/options`, {
        ...(rule.options || {}),
        [def.key]: value,
      });
      notify(def.message(value), { type: 'success' });
      onChanged?.();
    } catch (err: any) {
      notify(`Failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <TableRow>
      <TableCell colSpan={5} sx={{ py: 1, pl: 6, borderBottom: `1px solid ${colors.border}` }}>
        <Stack direction="row" spacing={3} alignItems="center">
          <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600 }}>
            Options:
          </Typography>
          <FormControlLabel
            control={<Switch size="small" checked={checked} disabled={busy} onChange={(e) => toggle(e.target.checked)} />}
            label={<Typography variant="caption" sx={{ color: colors.textSecondary }}>{def.label}</Typography>}
          />
        </Stack>
      </TableCell>
    </TableRow>
  );
}
